// MUI Imports
import { useTheme } from '@mui/material/styles'

// Third-party Imports
import PerfectScrollbar from 'react-perfect-scrollbar'

// Type Imports
import type { VerticalMenuContextProps } from '@menu/components/vertical-menu/Menu'

// Component Imports
import { Menu, SubMenu, MenuSection, MenuItem } from '@menu/vertical-menu'

// Hook Imports
import useVerticalNav from '@menu/hooks/useVerticalNav'

// Styled Component Imports
import StyledVerticalNavExpandIcon from '@menu/styles/vertical/StyledVerticalNavExpandIcon'

// Style Imports
import menuItemStyles from '@core/styles/vertical/menuItemStyles'
import menuSectionStyles from '@core/styles/vertical/menuSectionStyles'
import { Box } from '@mui/material'
import { useAppSelector } from '@/redux-store'
import { getFullName } from '@/utils/getInitials'
import { Auth, IUser } from '@/@data'
import LanguageDropdown from '@components/layout/shared/LanguageDropdown'
import { useTranslator } from '@/hooks/useTranslator'
import { useNavigate } from 'react-router-dom'
import { CaslCan } from '@/contexts/CaslProvider'

type RenderExpandIconProps = {
  open?: boolean
  transitionDuration?: VerticalMenuContextProps['transitionDuration']
}

type Props = {
  scrollMenu: (container: any, isPerfectScrollbar: boolean) => void
}

const RenderExpandIcon = ({ open, transitionDuration }: RenderExpandIconProps) => (
  <StyledVerticalNavExpandIcon open={open} transitionDuration={transitionDuration}>
    <i className='tabler-chevron-right' />
  </StyledVerticalNavExpandIcon>
)

// User Setting Menu Component
const UserSettingMenu = () => {
  const theme = useTheme()
  const verticalNavOptions = useVerticalNav()
  const { __ } = useTranslator()
  const { transitionDuration } = verticalNavOptions
  const user = useAppSelector(state => state.userReducer.user)
  const navigate = useNavigate()

  return (
    <Menu
      popoutMenuOffset={{ mainAxis: 23 }}
      menuItemStyles={menuItemStyles(verticalNavOptions, theme)}
      renderExpandIcon={({ open }) => <RenderExpandIcon open={open} transitionDuration={transitionDuration} />}
      renderExpandedMenuItemIcon={{ icon: <i className='tabler-circle text-xs' /> }}
      menuSectionStyles={menuSectionStyles(verticalNavOptions, theme)}
    >
      <MenuSection label={__('USER SETTING')}>
        {/* <SubMenu label={__('English')} icon={<i className='tabler-flag' />}>
          <MenuItem component={'span'} onClick={() => Auth.changeLanguage('en')}>
            {__('English')}
          </MenuItem>
          <MenuItem component={'span'} onClick={() => Auth.changeLanguage('vi')}>
            {__('Vietnamese')}
          </MenuItem>
        </SubMenu> */}
        <LanguageDropdown />

        <MenuItem
          href='/cms/profile'
          exactMatch={false}
          activeUrl='/cms/profile'
          icon={<i className='tabler-user-circle' />}
        >
          {getFullName(user as IUser)}
        </MenuItem>

        <MenuItem
          component={'span'}
          icon={<i className='tabler-logout' />}
          onClick={() => {
            Auth.handleLogout()
            navigate('/cms/login')
          }}
        >
          {__('Logout')}
        </MenuItem>
      </MenuSection>
    </Menu>
  )
}

const VerticalMenu = ({ scrollMenu }: Props) => {
  // Hooks
  const theme = useTheme()
  const verticalNavOptions = useVerticalNav()
  const { __ } = useTranslator()

  // Vars
  const { isBreakpointReached, transitionDuration } = verticalNavOptions

  const ScrollWrapper = isBreakpointReached ? 'div' : PerfectScrollbar

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%'
      }}
    >
      {/* Main Menu - Top */}
      <Box sx={{ flex: 1, overflow: 'hidden' }}>
        <ScrollWrapper
          {...(isBreakpointReached
            ? {
                className: 'bs-full overflow-y-auto overflow-x-hidden',
                onScroll: container => scrollMenu(container, false)
              }
            : {
                options: { wheelPropagation: false, suppressScrollX: true },
                onScrollY: container => scrollMenu(container, true)
              })}
        >
          <Menu
            popoutMenuOffset={{ mainAxis: 23 }}
            menuItemStyles={menuItemStyles(verticalNavOptions, theme)}
            renderExpandIcon={({ open }) => <RenderExpandIcon open={open} transitionDuration={transitionDuration} />}
            renderExpandedMenuItemIcon={{ icon: <i className='tabler-circle text-xs' /> }}
            menuSectionStyles={menuSectionStyles(verticalNavOptions, theme)}
          >
            {/* <MenuSection label={__('DASHBOARDS')}>
              <MenuItem href='/cms/analytics' icon={<i className='tabler-smart-home' />}>
                {__('Analytics & Report')}
              </MenuItem>
            </MenuSection> */}

            <MenuSection label={__('MENU')}>
              <CaslCan action='get' subject='user_management'>
                <SubMenu label={__('Users')} icon={<i className='tabler-users' />}>
                  <CaslCan action='get' subject='employee_list'>
                    <MenuItem href='/cms/users/employee' exactMatch={false} activeUrl='/cms/users/employee'>
                      {__('Employee')}
                    </MenuItem>
                  </CaslCan>
                  <CaslCan action='get' subject='lift_list'>
                    <MenuItem href='/cms/users/lift-user' exactMatch={false} activeUrl='/cms/users/lift-user'>
                      {__('Lift User')}
                    </MenuItem>
                  </CaslCan>
                </SubMenu>
              </CaslCan>

              <CaslCan action='get' subject='employer_management'>
                <MenuItem
                  href='/cms/employer'
                  exactMatch={false}
                  activeUrl='/cms/employer'
                  icon={<i className='tabler-briefcase' />}
                >
                  {__('Employer')}
                </MenuItem>
              </CaslCan>

              <CaslCan action='get' subject='attendance_management'>
                <MenuItem
                  href='/cms/attendance'
                  exactMatch={false}
                  activeUrl='/cms/attendance'
                  icon={<i className='tabler-calendar' />}
                  // suffix={<CustomChip label='1' size='small' color='primary' round='true' />}
                  // onClick={() => {
                  //   console.log('Attendance')
                  // }}
                >
                  {__('Attendance')}
                </MenuItem>
              </CaslCan>
              <CaslCan action='get' subject='finance_management'>
                <SubMenu label={__('Finance')} icon={<i className='tabler-building-bank' />}>
                  <CaslCan action='get' subject='transaction_list'>
                    <MenuItem href='/cms/finance/transaction' exactMatch={false} activeUrl='/cms/finance/transaction'>
                      {__('Transaction')}
                    </MenuItem>
                  </CaslCan>
                  <CaslCan action='get' subject='balance_list'>
                    <MenuItem href='/cms/finance/balance' exactMatch={false} activeUrl='/cms/finance/balance'>
                      {__('Balance')}
                    </MenuItem>
                  </CaslCan>
                  <CaslCan action='get' subject='voucher_list'>
                    <MenuItem href='/cms/finance/voucher' exactMatch={false} activeUrl='/cms/finance/voucher'>
                      {__('Voucher')}
                    </MenuItem>
                  </CaslCan>
                </SubMenu>
              </CaslCan>
              {/* <MenuItem
                href='/cms/settlement'
                exactMatch={false}
                activeUrl='/cms/settlement'
                icon={<i className='tabler-file-dollar' />}
              >
                {__('Settlement')}
              </MenuItem> */}
            </MenuSection>
          </Menu>
        </ScrollWrapper>
      </Box>

      {/* User Setting Menu - Bottom */}
      <Box
        sx={{
          paddingBottom: '8px',
          width: '100%',
          flexShrink: 0,
          height: 'auto',
          marginBottom: '10px'
        }}
      >
        <UserSettingMenu />
      </Box>
    </Box>
  )
}

export default VerticalMenu
